// Feature flags for the 8 top-level features that can be turned off at
// deploy time. Reads Vite build-time env vars — set VITE_FEATURE_<KEY>_ENABLED
// to the literal string 'true' to enable the feature. Any other value
// (or missing entirely) leaves the feature disabled. Default = OFF.
//
// The env-var reads MUST be written as literal `import.meta.env.VITE_FOO`
// references so Vite can statically inline them at build time — dynamic
// `import.meta.env[key]` access doesn't get replaced and would evaluate
// against `process.env` at runtime, which is undefined in the browser. So
// the mapping table is spelled out one line per flag.

export type FeatureKey =
    | 'tasks'
    | 'assets'
    | 'controlMeasures'
    | 'assetTypes'
    | 'sensorTypes'
    | 'rfc'
    | 'intel'
    | 'jipoe';

const FEATURE_ENV: Record<FeatureKey, string | undefined> = {
    tasks: import.meta.env.VITE_FEATURE_TASKS_ENABLED,
    assets: import.meta.env.VITE_FEATURE_ASSETS_ENABLED,
    controlMeasures: import.meta.env.VITE_FEATURE_CONTROL_MEASURES_ENABLED,
    assetTypes: import.meta.env.VITE_FEATURE_ASSET_TYPES_ENABLED,
    sensorTypes: import.meta.env.VITE_FEATURE_SENSOR_TYPES_ENABLED,
    rfc: import.meta.env.VITE_FEATURE_RFC_ENABLED,
    intel: import.meta.env.VITE_FEATURE_INTEL_ENABLED,
    jipoe: import.meta.env.VITE_FEATURE_JIPOE_ENABLED,
};

export function isFeatureEnabled(feature: FeatureKey): boolean {
    return FEATURE_ENV[feature] === 'true';
}

/** Map of route name → feature key. Consumed by the router guard so a
 *  direct URL hit / deep link to a disabled feature bounces the user
 *  back to '/' instead of rendering the page under a hidden menu. */
export const routeFeature: Record<string, FeatureKey> = {
    // Tasks
    CatlTasks: 'tasks',
    TaskCreateManual: 'tasks',
    NodeHistory: 'tasks',
    // Assets
    CatlAssets: 'assets',
    AssetCreate: 'assets',
    AssetEdit: 'assets',
    AssetView: 'assets',
    ActiveSortie: 'assets',
    // Control Measures
    CatlControlMeasures: 'controlMeasures',
    ControlMeasureCreate: 'controlMeasures',
    ControlMeasureView: 'controlMeasures',
    ControlMeasureEdit: 'controlMeasures',
    // Asset Types
    CatlAssetTypes: 'assetTypes',
    AssetTypeCreate: 'assetTypes',
    AssetTypeEdit: 'assetTypes',
    // Sensor Types
    CatlSensorTypes: 'sensorTypes',
    // RFC (Request for Collection) — includes the Sync Matrix which is
    // captioned "Requests for Collection" in its route title.
    CatlRfc: 'rfc',
    CatlSyncMatrix: 'rfc',
    // Intel
    IntelSources: 'intel',
    // JIPOE
    Jipoe: 'jipoe',
};
